import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Pencil } from "lucide-react";
import {
  CreateItemParams,
  Item,
  Permission,
  UpdateItemParams,
} from "@forgepoint/shared";
import { useItemsStore } from "@/modules/items/itemsStore";
import ItemEditor from "@/modules/items/ItemEditor";
import { EntitySelector } from "@/components/EntitySelector";
import { Button } from "@/components/ui/button";
import { EntityCreateRow } from "@/components/entitySelector/EntitySelectorCreateRows";
import { ItemRowMeta } from "@/components/itemSelector/ItemRowMeta";
import { ItemRowPrice } from "@/components/itemSelector/ItemRowPrice";
import { useRecentItems } from "@/components/itemSelector/useRecentItems";
import { useHasPermission } from "@/store/authStore";
import { cn } from "@/lib/utils";

const ITEMS_PAGE_SIZE = 50;
const POPOVER_MIN_WIDTH = 380;

interface ItemSelectorProps {
  currentlySelectedId: string | null;
  onSelectId: (itemId: string | null) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  variant?: "default" | "ghost";
  excludeItemId?: string;
  /** Shows the create row in the dropdown, subject to the items edit permission. */
  enableQuickCreate?: boolean;
  /** Shows the unit cost on the closed trigger. Turn off where the surrounding view already shows prices. */
  showTriggerCost?: boolean;
  /** Shows the edit button opening the item editor for the current selection. */
  enableEdit?: boolean;
}

/**
 * Searchable item picker showing each item's description, unit cost at quantity 1
 * and freshness, with paging, a recently used group and an inline create action.
 * @param props - The current selection, change handler and presentation options
 * @returns The item selector and the editor sheets for creating and editing items
 */
export function ItemSelector({
  currentlySelectedId,
  onSelectId,
  placeholder = "Select an item...",
  className,
  disabled = false,
  variant = "default",
  excludeItemId,
  enableQuickCreate = true,
  showTriggerCost = true,
  enableEdit = true,
}: ItemSelectorProps) {
  const { items, listLoading, fetchItems, fetchItem, createItem, updateItem } =
    useItemsStore();
  const canEditItems = useHasPermission(Permission.ITEMS_EDIT);
  const showCreateRows = enableQuickCreate && canEditItems;

  const { recentItemIds, pushRecentItem } = useRecentItems();

  const [loadedCount, setLoadedCount] = useState(0);
  const [total, setTotal] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  // Keep the request offset in sync with the active search before React renders.
  const paging = useRef({
    search: undefined as string | undefined,
    loadedCount: 0,
    total: 0,
    loading: false,
  });

  const [isCreateSheetOpen, setIsCreateSheetOpen] = useState(false);
  const [createSheetName, setCreateSheetName] = useState("");
  const [isEditSheetOpen, setIsEditSheetOpen] = useState(false);

  useEffect(() => {
    if (currentlySelectedId && !items.has(currentlySelectedId)) {
      fetchItem(currentlySelectedId);
    }
  }, [currentlySelectedId, fetchItem, items]);

  const fetchEntities = useCallback(
    async (search?: string) => {
      const page = { search, loadedCount: 0, total: 0, loading: true };
      paging.current = page;
      setLoadedCount(0);
      setTotal(0);
      setIsLoadingMore(false);
      try {
        const result = await fetchItems({
          search,
          limit: ITEMS_PAGE_SIZE,
          offset: 0,
        });
        if (paging.current === page) {
          page.loadedCount = result.ids.length;
          page.total = result.total;
          setLoadedCount(page.loadedCount);
          setTotal(page.total);
        }
        return result.ids;
      } finally {
        page.loading = false;
      }
    },
    [fetchItems],
  );

  const handleLoadMore = useCallback(
    async (search?: string) => {
      const page = paging.current;
      if (
        page.search !== search ||
        page.loading ||
        page.loadedCount >= page.total
      ) {
        return [];
      }
      page.loading = true;
      setIsLoadingMore(true);
      try {
        const result = await fetchItems({
          search,
          limit: ITEMS_PAGE_SIZE,
          offset: page.loadedCount,
        });
        if (paging.current === page) {
          page.loadedCount += result.ids.length;
          page.total = result.total;
          setLoadedCount(page.loadedCount);
          setTotal(page.total);
        }
        return result.ids;
      } finally {
        page.loading = false;
        if (paging.current === page) {
          setIsLoadingMore(false);
        }
      }
    },
    [fetchItems],
  );

  const handleSelectId = useCallback(
    (itemId: string | null) => {
      onSelectId(itemId);
      if (itemId) {
        pushRecentItem(itemId);
      }
    },
    [onSelectId, pushRecentItem],
  );

  const handleCreate = useCallback((query: string, close: () => void) => {
    close();
    setCreateSheetName(query.trim());
    setIsCreateSheetOpen(true);
  }, []);

  // A stable reference, so the sheet's seeding effect cannot re-run and discard
  // whatever the user has typed into the create form.
  const createSheetInitialValues = useMemo<Partial<CreateItemParams>>(
    () => ({ name: createSheetName }),
    [createSheetName],
  );

  const handleSaveCreate = useCallback(
    async (data: CreateItemParams): Promise<true | string> => {
      const { item, error } = await createItem(data);
      if (!item) {
        return error ?? "Failed to create item";
      }
      handleSelectId(item.id);
      return true;
    },
    [createItem, handleSelectId],
  );

  const handleSaveEdit = useCallback(
    async (data: UpdateItemParams): Promise<true | string> => {
      if (!currentlySelectedId) {
        return "No item selected";
      }
      const { item, error } = await updateItem(currentlySelectedId, data);
      return item ? true : (error ?? "Failed to update item");
    },
    [currentlySelectedId, updateItem],
  );

  const createRows = useMemo<EntityCreateRow[] | undefined>(() => {
    if (!showCreateRows) {
      return undefined;
    }
    return [
      {
        key: "create-item",
        label: (query) => `Create item "${query}"...`,
        onSelect: handleCreate,
      },
    ];
  }, [showCreateRows, handleCreate]);

  const showEditButton = enableEdit && !!currentlySelectedId;

  return (
    <>
      <div className="flex w-full items-center gap-1">
        <EntitySelector<Item>
          entities={items}
          listLoading={listLoading && !isLoadingMore}
          fetchEntities={fetchEntities}
          getDisplayName={getItemName}
          renderRowMeta={renderItemMeta}
          renderRowPrice={renderItemRowPrice}
          renderTriggerValue={
            showTriggerCost ? renderItemTriggerPrice : undefined
          }
          createRows={createRows}
          onLoadMore={handleLoadMore}
          hasMore={loadedCount < total}
          isLoadingMore={isLoadingMore}
          popoverMinWidth={POPOVER_MIN_WIDTH}
          pinnedEntityIds={recentItemIds}
          pinnedGroupLabel="Recently used"
          currentlySelectedId={currentlySelectedId}
          onSelectId={handleSelectId}
          placeholder={placeholder}
          searchPlaceholder="Search items..."
          className={cn("min-w-0 flex-1", className)}
          disabled={disabled}
          variant={variant}
          excludeId={excludeItemId}
          entityLabel="items"
        />
        {showEditButton && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            disabled={disabled}
            onClick={() => setIsEditSheetOpen(true)}
            aria-label="Edit item"
            title="Edit item"
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
      {showCreateRows && (
        <ItemEditor
          open={isCreateSheetOpen}
          onOpenChange={setIsCreateSheetOpen}
          initialValues={createSheetInitialValues}
          onSave={handleSaveCreate}
          mode="create"
        />
      )}
      {showEditButton && currentlySelectedId && (
        <ItemEditor
          open={isEditSheetOpen}
          onOpenChange={setIsEditSheetOpen}
          itemId={currentlySelectedId}
          onSave={handleSaveEdit}
          mode="edit"
        />
      )}
    </>
  );
}

/**
 * Builds an item's display name, appending its revision when it has one.
 * @param item - The item to label
 * @returns The item name, suffixed with "Rev {revision}" when set
 */
function getItemName(item: Item): string {
  return item.revision ? `${item.name} Rev ${item.revision}` : item.name;
}

/**
 * Renders the secondary lines of an item row.
 * @param item - The item to describe
 * @returns The description, type and freshness lines
 */
function renderItemMeta(item: Item) {
  return <ItemRowMeta item={item} />;
}

/**
 * Renders the right-aligned cost block of an item row.
 * @param item - The item to price
 * @returns The stacked unit cost and its label
 */
function renderItemRowPrice(item: Item) {
  return <ItemRowPrice item={item} variant="row" />;
}

/**
 * Renders the trailing cost on the closed trigger.
 * @param item - The item to price
 * @returns The single-line unit cost and its label
 */
function renderItemTriggerPrice(item: Item) {
  return <ItemRowPrice item={item} variant="trigger" />;
}
